import type { Tag } from './types';

export const DISCUSSION_CATEGORY_ROWS = [
  ['configurator', 'factorytrack', 'infor-document-management', 'syteline-v10', 'syteline-v9'],
  ['mt-cloud', 'on-premise'],
  ['general', 'finance', 'hr', 'manufacturing', 'sales', 'technical']
];

export const DISCUSSION_CATEGORY_ROW_LABELS = ['Core systems', 'Deployment', 'Business areas'];

export const DISCUSSION_CATEGORY_LABELS: Record<string, string> = {
  configurator: 'Configurator',
  factorytrack: 'FactoryTrack',
  'infor-document-management': 'Infor Document Management',
  'syteline-v10': 'Syteline v10',
  'syteline-v9': 'Syteline v9',
  'mt-cloud': 'MT Cloud',
  'on-premise': 'On Premise',
  general: 'User Group',
  finance: 'Finance',
  hr: 'HR',
  manufacturing: 'Manufacturing',
  sales: 'Sales',
  technical: 'Technical / IT'
};

export function normalizeDiscussionCategoryId(value: string | undefined | null) {
  return String(value || '')
    .trim()
    .replace(/^discussion:/i, '')
    .toLowerCase();
}

const DEFAULT_CATEGORY_PLACEMENT = new Map<string, { displayRow: number; displayOrder: number }>();
DISCUSSION_CATEGORY_ROWS.forEach((row, rowIndex) => {
  row.forEach((id, itemIndex) => {
    DEFAULT_CATEGORY_PLACEMENT.set(id, {
      displayRow: rowIndex + 1,
      displayOrder: (itemIndex + 1) * 10
    });
  });
});

function finiteNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function discussionTagDisplayRow(tag: Pick<Tag, 'id' | 'displayRow'>) {
  const explicit = finiteNumber(tag.displayRow);
  if (explicit !== null && explicit > 0) return Math.max(1, Math.trunc(explicit));
  return DEFAULT_CATEGORY_PLACEMENT.get(normalizeDiscussionCategoryId(tag.id))?.displayRow ?? DISCUSSION_CATEGORY_ROWS.length + 1;
}

export function discussionTagDisplayOrder(tag: Pick<Tag, 'id' | 'displayOrder'>) {
  const explicit = finiteNumber(tag.displayOrder);
  if (explicit !== null) return explicit;
  return DEFAULT_CATEGORY_PLACEMENT.get(normalizeDiscussionCategoryId(tag.id))?.displayOrder ?? 9999;
}

function withNormalizedTagId(tag: Tag): Tag {
  const id = normalizeDiscussionCategoryId(tag.id);
  return {
    ...tag,
    id,
    slug: id,
    name: DISCUSSION_CATEGORY_LABELS[id] ?? tag.name,
    displayRow: discussionTagDisplayRow({ id, displayRow: tag.displayRow }),
    displayOrder: discussionTagDisplayOrder({ id, displayOrder: tag.displayOrder })
  };
}

export function sortDiscussionTagsForDisplay(tags: Tag[]) {
  return [...tags].map(withNormalizedTagId).sort((a, b) => {
    const rowDiff = discussionTagDisplayRow(a) - discussionTagDisplayRow(b);
    if (rowDiff !== 0) return rowDiff;

    const orderDiff = discussionTagDisplayOrder(a) - discussionTagDisplayOrder(b);
    if (orderDiff !== 0) return orderDiff;

    return a.name.localeCompare(b.name);
  });
}

export function valueForDiscussionCategory<T>(values: Record<string, T>, id: string | undefined | null) {
  const rawId = String(id || '').trim();
  const normalizedId = normalizeDiscussionCategoryId(rawId);
  return values[rawId] ?? values[normalizedId] ?? values[`discussion:${normalizedId}`];
}

function fallbackCategoryLabel(id: string) {
  const normalizedId = normalizeDiscussionCategoryId(id);
  return (
    DISCUSSION_CATEGORY_LABELS[normalizedId] ??
    normalizedId
      .split(/[-_]/)
      .filter(Boolean)
      .map((part) => (part.length <= 3 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1)))
      .join(' ')
  );
}

export function groupDiscussionTags(tags: Tag[]) {
  const rows = new Map<number, Tag[]>();

  for (const tag of sortDiscussionTagsForDisplay(tags)) {
    const row = discussionTagDisplayRow(tag);
    rows.set(row, [...(rows.get(row) ?? []), tag]);
  }

  return Array.from(rows.entries())
    .sort(([a], [b]) => a - b)
    .map(([, rowTags]) => rowTags);
}

export function resolveDiscussionTopicTags(tagIds: string[] | undefined, tags: Tag[]) {
  const byId = new Map<string, Tag>();
  for (const tag of tags) {
    const normalizedTag = withNormalizedTagId(tag);
    byId.set(String(tag.id || '').trim(), normalizedTag);
    byId.set(normalizedTag.id, normalizedTag);
  }
  const seen = new Set<string>();
  const resolved: Tag[] = [];

  for (const rawId of tagIds ?? []) {
    const id = normalizeDiscussionCategoryId(rawId);
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const tag = byId.get(String(rawId || '').trim()) ?? byId.get(id);
    resolved.push(
      tag ?? {
        id,
        name: fallbackCategoryLabel(id),
        slug: id,
        scope: 'discussion'
      }
    );
  }

  return resolved;
}
